import React, { useState, useEffect } from 'react';
import DialogComponent from '../dialog/dialog.component';
import toast from 'react-hot-toast';
import {
  createWithdrawRequest,
  estimateWithdrawFee,
  WithdrawFeeEstimate,
} from '../../api/payment/transaction.api';
import { getBankAccount } from '../../api/admin/user.api';
import { fetchBalance } from '../../api';
import WithdrawFeeScheduleBox from '../withdraw-fee-schedule/withdraw-fee-schedule';
import { getUserBalanceBuckets, UserBalanceBuckets } from '../../api/payment/store.api';
import { InputNumber } from 'antd';
import { InputWarning } from '../inputwarning/input-warning.component';
import { TrapeziumButton } from '../buton-trapezium';
import { EkycCameraModal } from '../../pages/donate-goal/ekyc-camera-modal';
import { getEkycStatus } from '../../api/user/ekyc.api';
import { updateBankAccount } from '../../api/payment/user-bank.api';

const fmtCa = (n: number) => `${Number(n).toLocaleString('vi-VN')} ca`;
const fmtVnd = (n: number) => `${Number(n).toLocaleString('vi-VN')}đ`;
// Mức tối thiểu thật lấy từ máy chủ (estimate trả minCa theo lần đầu/lần sau). Mặc định theo cấu hình hiện hành
// (1 Ca = 1đ): lần sau 100.000 Ca, lần đầu 300.000 Ca. Trước đây web còn ghi và chặn theo tỉ giá cũ 1 Ca = 10đ.
const DEFAULT_MIN_CA = 100000;

const DialogWithdrawal = ({ onCancel }: { onCancel: () => void }) => {
  const [open, setOpen] = useState(false);
  const [ekycOpen, setEkycOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [taxConsent, setTaxConsent] = useState(false); // cam kết tự chịu trách nhiệm thuế TNCN

  // Bấm "Rút tiền": nếu chưa eKYC thì mở luồng chụp CCCD + khuôn mặt trước, xong mới cho rút.
  const handleOpenWithdraw = async () => {
    try {
      const st = await getEkycStatus();
      if (st?.isVerified) setOpen(true);
      else setEkycOpen(true);
    } catch {
      setEkycOpen(true);
    }
  };
  const [balance, setBalance] = useState(0);
  const [buckets, setBuckets] = useState<UserBalanceBuckets | null>(null);
  // Chỉ Ca thu nhập (nhận donate, đã qua hạn giữ, chưa rút) mới rút được; Ca nạp không rút được.
  // Máy chủ trả sẵn "withdrawable" cùng công thức với lúc tạo lệnh (rà soát 28/10: trước đây hiện cả Ca còn trong hạn giữ).
  const withdrawable = buckets?.withdrawable ?? buckets?.incomeBalance ?? balance;
  const [estimate, setEstimate] = useState<WithdrawFeeEstimate | null>(null);
  const [minCa, setMinCa] = useState<number | null>(null);
  const [isFirstWithdraw, setIsFirstWithdraw] = useState<boolean | null>(null);
  const minWithdraw = estimate?.minCa ?? minCa ?? DEFAULT_MIN_CA;
  const [formData, setFormData] = useState({
    amount: '',
    bankName: '',
    bankAccountNumber: '',
    bankAccountName: '',
    note: '',
  });

  // Bóc tách phí rút real-time từ BE (debounce 400ms) khi nhập số Ca.
  useEffect(() => {
    const amount = Number(formData.amount);
    if (!open || !amount || amount <= 0) {
      setEstimate(null);
      return;
    }
    const t = setTimeout(() => {
      estimateWithdrawFee(amount)
        .then(setEstimate)
        .catch(() => setEstimate(null));
    }, 400);
    return () => clearTimeout(t);
  }, [formData.amount, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClose = () => {
    setOpen(false);
    setTaxConsent(false);
  };

  // Số dư & số rút lưu bằng Ca → hiển thị "... ca"
  const formatCa = (value) => `${Number(value).toLocaleString('vi-VN')} ca`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!taxConsent) {
      toast.error('Vui lòng tích chọn cam kết tự chịu trách nhiệm về nghĩa vụ thuế cá nhân');
      return;
    }

    const amount = Number(formData.amount);
    if (amount <= 0) {
      toast.error('Số Ca phải lớn hơn 0');
      return;
    }

    if (amount < minWithdraw) {
      toast.error(`Số Ca rút tối thiểu là ${fmtCa(minWithdraw)} (${fmtVnd(minWithdraw)}).`);
      return;
    }

    if (amount > withdrawable) {
      toast.error('Số Ca rút vượt quá Ca thu nhập (chỉ Ca nhận từ Donate mới rút được; Ca nạp không rút được)');
      return;
    }

    try {
      setIsSubmitting(true);
      await createWithdrawRequest({
        ...formData,
        amount,
      });
      toast.success('Yêu cầu quyết toán thù lao đã được gửi');
      handleClose();
    } catch (error: any) {
      // Hiện đúng lý do từ BE (vd: Ca nạp không rút được, chưa eKYC, tên không khớp TK ngân hàng)
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.response?.data?.title ||
        'Có lỗi xảy ra khi gửi yêu cầu quyết toán thù lao';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchBankAccount = async () => {
    try {
      const data = await getBankAccount();
      setFormData((prev) => ({
        ...prev,
        bankName: data.bankName,
        bankAccountNumber: data.bankAccountNumber,
        bankAccountName: data.bankAccountName,
      }));
    } catch (error) {
      console.error('Error fetching bank account:', error);
    }
  };

  useEffect(() => {
    if (open) {
      fetchBankAccount();
      fetchBalance().then((res) => {
        setBalance(res.balance);
      });
      getUserBalanceBuckets().then(setBuckets).catch(() => setBuckets(null));
      estimateWithdrawFee(0)
        .then((e) => {
          setMinCa(e?.minCa ?? null);
          setIsFirstWithdraw(typeof e?.isFirstWithdraw === 'boolean' ? e.isFirstWithdraw : null);
        })
        .catch(() => {
          setMinCa(null);
          setIsFirstWithdraw(null);
        });
    }
  }, [open]);

  return (
    <>
      <TrapeziumButton onClick={handleOpenWithdraw}>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6.88169 17.7056C7.4208 17.1664 7.7305 16.478 7.8108 15.7668C7.91977 14.8203 7.60433 13.8394 6.88169 13.1167C6.45155 12.6865 5.9239 12.3997 5.36759 12.2678C4.30657 11.9982 3.13085 12.2792 2.2935 13.1167C1.71424 13.696 1.39881 14.436 1.35866 15.1989C1.32998 15.5259 1.35866 15.8643 1.44469 16.1912C1.5766 16.7476 1.86336 17.2754 2.2935 17.7056C3.56099 18.9732 5.6142 18.9732 6.88169 17.7056ZM5.80346 14.8146C6.13611 14.8146 6.4114 15.0899 6.4114 15.4226C6.40566 15.761 6.13611 16.0306 5.79773 16.0364L5.19553 16.0306L5.20126 16.61C5.19553 16.9484 4.92597 17.218 4.58759 17.2237C4.24922 17.218 3.97966 16.9484 3.97393 16.61L3.97966 16.0306L3.37746 16.0364C3.03908 16.0306 2.76953 15.761 2.76379 15.4226C2.76953 15.2563 2.83835 15.1071 2.94732 14.9981C3.05629 14.8891 3.2054 14.8203 3.37173 14.8146L3.97966 14.8146L3.97966 14.1836C3.97966 14.0115 4.04848 13.8624 4.15745 13.7534C4.26642 13.6444 4.41554 13.5756 4.58759 13.5756C4.92024 13.5756 5.19553 13.8509 5.19553 14.1836L5.19553 14.8146L5.80346 14.8146Z"
            fill="white"
          />
          <path
            d="M12.5794 3.20535V6.28793H11.3628V3.20535C11.3628 2.98633 11.1681 2.88087 11.0383 2.88087C10.9978 2.88087 10.9572 2.88898 10.9167 2.90521L4.48477 5.33071C4.0549 5.49295 3.77913 5.89855 3.77913 6.36094V6.90444C3.04104 7.45606 2.5625 8.34028 2.5625 9.33806V6.36094C2.5625 5.3956 3.15459 4.53573 4.0549 4.19502L10.4949 1.76141C10.6733 1.69651 10.8599 1.66406 11.0383 1.66406C11.8494 1.66406 12.5794 2.32114 12.5794 3.20535Z"
            fill="white"
          />
          <path
            d="M17.9741 11.764V12.5752C17.9741 12.7942 17.8037 12.9727 17.5766 12.9808H16.3925C15.9626 12.9808 15.5733 12.6644 15.5408 12.2426C15.5165 11.9911 15.6138 11.7559 15.776 11.5936C15.922 11.4395 16.1248 11.3584 16.3438 11.3584H17.5685C17.8037 11.3665 17.9741 11.545 17.9741 11.764Z"
            fill="white"
          />
          <path
            d="M16.3347 10.5054H17.162C17.6081 10.5054 17.9731 10.1403 17.9731 9.69417V9.33724C17.9731 7.65805 16.6024 6.28711 14.9234 6.28711H5.61218C4.92276 6.28711 4.29011 6.51425 3.77913 6.90363C3.04104 7.45524 2.5625 8.33946 2.5625 9.33724V10.7812C2.5625 11.0894 2.88693 11.2841 3.17892 11.1868C3.63313 11.0327 4.11167 10.9515 4.59021 10.9515C7.0478 10.9515 9.05118 12.9552 9.05118 15.4132C9.05118 15.9972 8.89707 16.6381 8.65374 17.2059C8.52397 17.498 8.72674 17.8468 9.04306 17.8468H14.9234C16.6024 17.8468 17.9731 16.4758 17.9731 14.7966V14.6425C17.9731 14.1964 17.6081 13.8313 17.162 13.8313H16.4564C15.6777 13.8313 14.9315 13.3527 14.7288 12.5983C14.5666 11.9818 14.7612 11.3815 15.1668 10.9921C15.4669 10.6838 15.8805 10.5054 16.3347 10.5054ZM11.89 10.3431H7.83455C7.502 10.3431 7.22624 10.0673 7.22624 9.73473C7.22624 9.40214 7.502 9.12633 7.83455 9.12633H11.89C12.2225 9.12633 12.4983 9.40214 12.4983 9.73473C12.4983 10.0673 12.2225 10.3431 11.89 10.3431Z"
            fill="white"
          />
        </svg>
        <span className="skew-x-[20deg]"> Quyết toán thù lao</span>
      </TrapeziumButton>

      <DialogComponent
        onClose={() => handleClose()}
        visible={open}
        placement="CENTER"
        title="Yêu cầu quyết toán thù lao"
        size="xl"
      >
        <div>
          <div className="w-full h-12 flex justify-between px-5 items-center bg-[#ebecf2]">
            <span className="text-sm text-[#111827]  font-semibold">
              Yêu cầu quyết toán thù lao
            </span>
            <div className="flex items-center gap-1">
              <svg
                width="17"
                height="16"
                viewBox="0 0 17 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.5 12L12.5 4"
                  stroke="#26B35E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6 4H12.5V10.5"
                  stroke="#26B35E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="text-sm font-medium uppercase text-[#26B35E]">
                Có thể rút {formatCa(withdrawable)}
              </span>
            </div>
          </div>

          {/* Giải thích 2 loại Ca để creator hiểu vì sao Ca nạp không rút được */}
          <div className="mx-5 mt-3 grid grid-cols-2 gap-2 text-[13px]">
            <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] p-2.5">
              <div className="text-[#92400E]">💳 Ca nạp (không rút được)</div>
              <div className="font-semibold text-[#111827]">{fmtCa(buckets?.topUpBalance ?? 0)}</div>
              <div className="text-[11px] text-[#92400E]">Chỉ dùng để donate &amp; mua sắm</div>
            </div>
            <div className="rounded-lg border border-[#A7F3D0] bg-[#ECFDF5] p-2.5">
              <div className="text-[#065F46]">🎁 Ca thu nhập (rút được)</div>
              <div className="font-semibold text-[#111827]">{fmtCa(withdrawable)}</div>
              <div className="text-[11px] text-[#065F46]">Nhận từ người khác Donate · đã qua 7 ngày</div>
            </div>
          </div>

          {/* Biểu phí hiện sẵn trước khi gõ số Ca, để creator biết phí và mức tối thiểu ngay từ đầu. */}
          <div className="mx-5 mt-3">
            <WithdrawFeeScheduleBox isFirstWithdraw={isFirstWithdraw} collapsible />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4 p-6">
              <div>
                <label className="flex items-center text-sm  text-[#070708] mb-1">
                  Số ca&nbsp;
                  <InputWarning />
                </label>
                <InputNumber
                  required
                  style={{
                    height: '48px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  name="amount"
                  value={formData.amount}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                  }
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      amount: value !== null ? value : '',
                    }))
                  }
                  className={`w-full ${
                    Number(formData.amount) > withdrawable ||
                    Number(formData.amount) < minWithdraw
                      ? 'border-red-500'
                      : 'border-gray-300'
                  }`}
                />
                {Number(formData.amount) > withdrawable && (
                  <p className="text-red-500 text-sm mt-1">
                    Vượt quá Ca thu nhập có thể rút ({formatCa(withdrawable)}). Ca nạp không rút được.
                  </p>
                )}
                {Number(formData.amount) < minWithdraw &&
                  Number(formData.amount) > 0 && (
                    <p className="text-red-500 text-sm mt-1">
                      Số Ca rút tối thiểu là {fmtCa(minWithdraw)} ({fmtVnd(minWithdraw)})
                    </p>
                  )}

                {estimate && (
                  <div className="mt-3 rounded-lg bg-[#F6F7F8] p-3 text-sm flex flex-col gap-1">
                    <div className="flex justify-between text-[#62677D]">
                      <span>Số Ca rút</span>
                      <span className="font-medium text-[#18191E]">
                        {fmtCa(estimate.amountCa)}
                      </span>
                    </div>
                    <div className="flex justify-between text-[#62677D]">
                      <span>Phí cố định</span>
                      <span>− {fmtCa(estimate.fixedFeeCa)}</span>
                    </div>
                    <div className="flex justify-between text-[#62677D]">
                      <span>
                        Phí xử lý ({estimate.feePercent}%
                        {estimate.isFirstWithdraw ? ' · lần đầu' : ''})
                      </span>
                      <span>− {fmtCa(estimate.percentFeeCa)}</span>
                    </div>
                    {estimate.personalIncomeTaxCa > 0 && (
                      <div className="flex justify-between text-[#62677D]">
                        <span>Thuế TNCN</span>
                        <span>− {fmtCa(estimate.personalIncomeTaxCa)}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t border-[#DEE0E6] dark:border-slate-700 pt-1 mt-1 font-semibold text-[#0071A6]">
                      <span>Thực nhận</span>
                      <span>
                        {fmtCa(estimate.netCa)} (≈ {fmtVnd(estimate.netVnd)})
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="flex items-center text-sm  text-[#070708] mb-1">
                  Tên ngân hàng&nbsp;
                  <InputWarning />
                </label>
                <input
                  placeholder="Nhập"
                  disabled
                  name="bankName"
                  value={formData.bankName}
                  onChange={handleChange}
                  className="w-full max-w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-700 dark:text-white disabled:opacity-70"
                />
              </div>

              <div>
                <label className="flex items-center text-sm  text-[#070708] mb-1">
                  Số tài khoản&nbsp;
                  <InputWarning />
                </label>
                <input
                  disabled
                  placeholder="Nhập"
                  name="bankAccountNumber"
                  value={formData.bankAccountNumber}
                  onChange={handleChange}
                  className="w-full p-3 h-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="flex items-center text-sm  text-[#070708] mb-1">
                  Tên chủ tài khoản&nbsp;
                  <InputWarning />
                </label>
                <input
                  disabled
                  placeholder="Nhập"
                  name="bankAccountName"
                  value={formData.bankAccountName}
                  onChange={handleChange}
                  className="w-full p-3 h-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="flex items-center text-sm  text-[#070708] mb-1">
                  Ghi chú
                </label>
                <textarea
                  name="note"
                  value={formData.note}
                  onChange={handleChange}
                  rows={3}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="mx-6 mb-1 rounded-lg border border-[#FED7AA] bg-[#FFF7ED] p-3">
              <label className="flex cursor-pointer items-start gap-2 text-[13px] leading-5 text-[#7C2D12]">
                <input
                  type="checkbox"
                  className="mt-0.5 size-4 shrink-0 accent-[#F04438]"
                  checked={taxConsent}
                  onChange={(e) => setTaxConsent(e.target.checked)}
                />
                <span>
                  Số Ca nhận từ Donate/Quyên góp là thu nhập/tài sản tặng cho hợp
                  pháp. Cab.vn chỉ hỗ trợ kỹ thuật chi trả, không chịu trách nhiệm
                  nghĩa vụ thuế cá nhân của tôi.{' '}
                  <b>Tôi đã đọc và cam kết tự chịu trách nhiệm về nghĩa vụ thuế cá nhân.</b>
                </span>
              </label>
            </div>

            <div className="flex border-t border-t-[#DEE0E6] dark:border-t-slate-700  w-full justify-between items-center p-6">
              <div className="flex gap-2">
                <p className="text-[#F04438] text-base font-semibold px-4 py-2 rounded-lg border border-[#DEE0E6] dark:border-slate-700 flex items-center justify-center">
                  Tối thiểu 100.000 ca (100.000đ) · lần đầu 300.000 ca (300.000đ)
                </p>
                <p className="text-gray -500 text-base px-4 py-2 rounded-lg border border-[#DEE0E6] dark:border-slate-700 flex items-center justify-center">
                  Phí rút: 5.000đ + 4% (lần đầu) / 1.5% (lần sau)
                </p>
              </div>
              <div className="flex items-center gap-4 ">
                <button
                  type="button"
                  className="bg-white border border-[#DEE0E6] dark:border-slate-700 text-base font-semibold h-10 flex items-center justify-center px-4 py-2 rounded-lg text-black"
                  onClick={() => {
                  // Mọi nơi dùng truyền onCancel rỗng nên nút không làm gì; tự đóng hộp rồi mới báo ra ngoài.
                  handleClose();
                  onCancel?.();
                }}
                >
                  Hủy bỏ
                </button>
                <TrapeziumButton type="submit" disabled={isSubmitting || !taxConsent}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6.88169 17.7056C7.4208 17.1664 7.7305 16.478 7.8108 15.7668C7.91977 14.8203 7.60433 13.8394 6.88169 13.1167C6.45155 12.6865 5.9239 12.3997 5.36759 12.2678C4.30657 11.9982 3.13085 12.2792 2.2935 13.1167C1.71424 13.696 1.39881 14.436 1.35866 15.1989C1.32998 15.5259 1.35866 15.8643 1.44469 16.1912C1.5766 16.7476 1.86336 17.2754 2.2935 17.7056C3.56099 18.9732 5.6142 18.9732 6.88169 17.7056ZM5.80346 14.8146C6.13611 14.8146 6.4114 15.0899 6.4114 15.4226C6.40566 15.761 6.13611 16.0306 5.79773 16.0364L5.19553 16.0306L5.20126 16.61C5.19553 16.9484 4.92597 17.218 4.58759 17.2237C4.24922 17.218 3.97966 16.9484 3.97393 16.61L3.97966 16.0306L3.37746 16.0364C3.03908 16.0306 2.76953 15.761 2.76379 15.4226C2.76953 15.2563 2.83835 15.1071 2.94732 14.9981C3.05629 14.8891 3.2054 14.8203 3.37173 14.8146L3.97966 14.8146L3.97966 14.1836C3.97966 14.0115 4.04848 13.8624 4.15745 13.7534C4.26642 13.6444 4.41554 13.5756 4.58759 13.5756C4.92024 13.5756 5.19553 13.8509 5.19553 14.1836L5.19553 14.8146L5.80346 14.8146Z"
                      fill="white"
                    />
                    <path
                      d="M12.5794 3.20535V6.28793H11.3628V3.20535C11.3628 2.98633 11.1681 2.88087 11.0383 2.88087C10.9978 2.88087 10.9572 2.88898 10.9167 2.90521L4.48477 5.33071C4.0549 5.49295 3.77913 5.89855 3.77913 6.36094V6.90444C3.04104 7.45606 2.5625 8.34028 2.5625 9.33806V6.36094C2.5625 5.3956 3.15459 4.53573 4.0549 4.19502L10.4949 1.76141C10.6733 1.69651 10.8599 1.66406 11.0383 1.66406C11.8494 1.66406 12.5794 2.32114 12.5794 3.20535Z"
                      fill="white"
                    />
                    <path
                      d="M17.9741 11.764V12.5752C17.9741 12.7942 17.8037 12.9727 17.5766 12.9808H16.3925C15.9626 12.9808 15.5733 12.6644 15.5408 12.2426C15.5165 11.9911 15.6138 11.7559 15.776 11.5936C15.922 11.4395 16.1248 11.3584 16.3438 11.3584H17.5685C17.8037 11.3665 17.9741 11.545 17.9741 11.764Z"
                      fill="white"
                    />
                    <path
                      d="M16.3347 10.5054H17.162C17.6081 10.5054 17.9731 10.1403 17.9731 9.69417V9.33724C17.9731 7.65805 16.6024 6.28711 14.9234 6.28711H5.61218C4.92276 6.28711 4.29011 6.51425 3.77913 6.90363C3.04104 7.45524 2.5625 8.33946 2.5625 9.33724V10.7812C2.5625 11.0894 2.88693 11.2841 3.17892 11.1868C3.63313 11.0327 4.11167 10.9515 4.59021 10.9515C7.0478 10.9515 9.05118 12.9552 9.05118 15.4132C9.05118 15.9972 8.89707 16.6381 8.65374 17.2059C8.52397 17.498 8.72674 17.8468 9.04306 17.8468H14.9234C16.6024 17.8468 17.9731 16.4758 17.9731 14.7966V14.6425C17.9731 14.1964 17.6081 13.8313 17.162 13.8313H16.4564C15.6777 13.8313 14.9315 13.3527 14.7288 12.5983C14.5666 11.9818 14.7612 11.3815 15.1668 10.9921C15.4669 10.6838 15.8805 10.5054 16.3347 10.5054ZM11.89 10.3431H7.83455C7.502 10.3431 7.22624 10.0673 7.22624 9.73473C7.22624 9.40214 7.502 9.12633 7.83455 9.12633H11.89C12.2225 9.12633 12.4983 9.40214 12.4983 9.73473C12.4983 10.0673 12.2225 10.3431 11.89 10.3431Z"
                      fill="white"
                    />
                  </svg>
                  <span className="skew-x-[20deg]">
                    {' '}
                    {isSubmitting ? 'Đang xử lý...' : 'Gửi yêu cầu quyết toán'}
                  </span>
                </TrapeziumButton>
                {/* <button
                  type="submit"
                  className="bg-white border border-[#DEE0E6] dark:border-slate-700 text-base font-semibold h-10 flex items-center justify-center px-4 py-2 rounded-lg text-black"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Đang xử lý...' : 'Xác nhận'}
                </button> */}
              </div>
            </div>
          </form>
        </div>
      </DialogComponent>

      {/* eKYC (chụp CCCD + khuôn mặt) — mở khi chưa xác thực mà bấm rút */}
      <EkycCameraModal
        open={ekycOpen}
        onClose={() => setEkycOpen(false)}
        onVerified={async ({ bank }) => {
          try {
            await updateBankAccount(bank);
            setEkycOpen(false);
            toast.success('Xác thực eKYC thành công');
            setOpen(true);
          } catch (e: any) {
            toast.error(
              e?.response?.data?.message || 'Lưu tài khoản ngân hàng thất bại'
            );
          }
        }}
      />
    </>
  );
};

export default DialogWithdrawal;
